import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, X, Loader2, ShoppingBag, Package, Upload, Image } from "lucide-react";

const MerchManager = () => {
  const { authAxios } = useAuth();
  const [merch, setMerch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    image_url: "",
    sizes: "S, M, L, XL",
    colors: "Black",
    category: "apparel",
    in_stock: true
  });

  useEffect(() => {
    fetchMerch();
  }, []);

  const fetchMerch = async () => {
    try {
      const response = await axios.get(`${API}/merchandise`);
      setMerch(response.data);
    } catch (e) {
      toast.error("Failed to fetch merchandise");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'merchandise');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Construct full URL
      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      setFormData({ ...formData, image_url: fullUrl });
      toast.success("Product image uploaded!");
    } catch (e) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        image_url: formData.image_url,
        sizes: formData.sizes.split(',').map(s => s.trim()).filter(s => s),
        colors: formData.colors.split(',').map(c => c.trim()).filter(c => c),
        category: formData.category,
        in_stock: formData.in_stock
      };
      
      await authAxios.post('/merchandise', payload);
      toast.success("Product added successfully!");
      setShowForm(false);
      resetForm();
      fetchMerch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add product");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await authAxios.delete(`/merchandise/${id}`);
      toast.success("Product deleted");
      fetchMerch();
    } catch (e) {
      toast.error("Failed to delete product");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      image_url: "",
      sizes: "S, M, L, XL",
      colors: "Black",
      category: "apparel",
      in_stock: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-yellow-400" />
          Merchandise ({merch.length})
        </h2>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-product-btn"
          className="cyber-btn py-2 px-4 text-xs border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD PRODUCT"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4 border-yellow-400/30" data-testid="product-form">
          {/* Product Image Upload Section */}
          <div className="border border-dashed border-yellow-400/50 p-4 mb-4">
            <Label className="font-orbitron text-sm text-yellow-400 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              PRODUCT IMAGE
            </Label>
            
            <div className="flex items-start gap-4">
              {/* Preview */}
              <div className="w-32 h-32 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {formData.image_url ? (
                  <img src={formData.image_url} alt="Product Preview" className="w-full h-full object-cover" />
                ) : (
                  <Image className="w-10 h-10 text-gray-600" />
                )}
              </div>

              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="product-photo-upload"
                />
                <label
                  htmlFor="product-photo-upload"
                  data-testid="product-upload-btn"
                  className={`cyber-btn py-3 px-4 text-xs cursor-pointer inline-flex items-center gap-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      UPLOADING...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      UPLOAD IMAGE
                    </>
                  )}
                </label>
                <p className="font-rajdhani text-xs text-gray-500">
                  JPG, PNG, GIF, WebP up to 5MB
                </p>
                <div className="space-y-1">
                  <p className="font-rajdhani text-xs text-gray-400">Or paste image URL</p>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://..."
                    className="bg-void-black border-white/10 text-sm"
                    data-testid="product-image-url-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Product Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="Product name"
                className="bg-void-black border-white/10"
                data-testid="product-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Price (USD) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                required
                placeholder="49.99"
                className="bg-void-black border-white/10"
                data-testid="product-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Sizes (comma separated)</Label>
              <Input
                value={formData.sizes}
                onChange={(e) => setFormData({...formData, sizes: e.target.value})}
                placeholder="S, M, L, XL"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Colors (comma separated)</Label>
              <Input
                value={formData.colors}
                onChange={(e) => setFormData({...formData, colors: e.target.value})}
                placeholder="Black, White"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Category</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                placeholder="apparel"
                className="bg-void-black border-white/10"
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={formData.in_stock}
                onCheckedChange={(v) => setFormData({...formData, in_stock: v})}
              />
              <Label className="font-rajdhani text-sm text-gray-400">In Stock</Label>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              placeholder="Product description..."
              rows={2}
              className="bg-void-black border-white/10 font-rajdhani"
              data-testid="product-description-input"
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="cyber-btn py-2 px-6 text-xs border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black" data-testid="save-product-btn">
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                SAVE PRODUCT
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {merch.map(item => (
          <div key={item.id} className="cyber-card overflow-hidden" data-testid={`product-item-${item.id}`}>
            <div className="aspect-square bg-white/5 relative">
              <img 
                src={item.image_url} 
                alt={item.name}
                className="w-full h-full object-cover"
              />
              {!item.in_stock && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <span className="font-orbitron text-red-500">SOLD OUT</span>
                </div>
              )}
              <div className="absolute top-2 right-2 bg-neon-pink px-2 py-1">
                <span className="font-orbitron text-xs text-white">${item.price.toFixed(2)}</span>
              </div>
            </div>
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-orbitron text-xs text-white truncate">{item.name}</h3>
                  <p className="font-rajdhani text-xs text-gray-500 mt-1">
                    {item.sizes.join(', ')} • {item.colors.join(', ')}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="text-gray-500 hover:text-red-500 transition-colors p-2 hover:bg-red-500/10 rounded flex-shrink-0"
                  data-testid={`delete-product-${item.id}`}
                  title="Delete product"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {merch.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-products-state">
          <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No products yet. Add your first item!</p>
        </div>
      )}
    </div>
  );
};

export default MerchManager;
